import { STORAGE_KEY } from "@/components/CookieConsent";

export type MarketingClickIds = {
  gaClientId?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  rdtCid?: string;
  twclid?: string;
};

const MAX_CLICK_ID_LENGTH = 255;
const META_FBC_PATTERN = /^fb\.1\.\d{10,13}\.[^\s]+$/;
const META_FBP_PATTERN = /^fb\.1\.\d{10,13}\.\d+$/;
export const MARKETING_CLICK_IDS_STORAGE_KEY = "mot.marketing-click-ids.v1";

function consentPreferences(): { analytics?: unknown; marketing?: unknown } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as { analytics?: unknown; marketing?: unknown } : null;
  } catch {
    return null;
  }
}

function gpcEnabled(): boolean {
  return typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
}

export function isMarketingConsentAllowed(): boolean {
  if (typeof window === "undefined" || gpcEnabled()) return false;
  return consentPreferences()?.marketing === true;
}

function boundedParam(params: URLSearchParams, name: string): string | undefined {
  const value = params.get(name)?.trim();
  return value && value.length <= MAX_CLICK_ID_LENGTH ? value : undefined;
}

function gaClientIdCookie(): string | undefined {
  if (consentPreferences()?.analytics !== true) return undefined;
  const cookieValue = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("_ga="))
    ?.slice(4)
    .trim();
  if (!cookieValue || cookieValue.length > MAX_CLICK_ID_LENGTH) return undefined;
  const match = /^GA\d+\.\d+\.(\d+\.\d+)$/.exec(cookieValue);
  return match?.[1];
}

function marketingCookie(name: "_fbc" | "_fbp"): string | undefined {
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1)
    .trim();
}

function metaFbcCookie(): string | undefined {
  const value = marketingCookie("_fbc");
  return value && value.length <= MAX_CLICK_ID_LENGTH && META_FBC_PATTERN.test(value) ? value : undefined;
}

function metaFbpCookie(): string | undefined {
  const value = marketingCookie("_fbp");
  return value && value.length <= MAX_CLICK_ID_LENGTH && META_FBP_PATTERN.test(value) ? value : undefined;
}

function fbcFromFbclid(fbclid: string): string | undefined {
  if (/\s/.test(fbclid)) return undefined;
  const fbc = `fb.1.${Math.floor(Date.now())}.${fbclid}`;
  return fbc.length <= MAX_CLICK_ID_LENGTH && META_FBC_PATTERN.test(fbc) ? fbc : undefined;
}

function storedClickIds(): MarketingClickIds | undefined {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(MARKETING_CLICK_IDS_STORAGE_KEY) ?? "null") as MarketingClickIds | null;
    if (!parsed || typeof parsed !== "object") return undefined;
    const clickIds: MarketingClickIds = {
      gaClientId: typeof parsed.gaClientId === "string" && parsed.gaClientId.length <= MAX_CLICK_ID_LENGTH ? parsed.gaClientId : undefined,
      fbclid: typeof parsed.fbclid === "string" && parsed.fbclid.length <= MAX_CLICK_ID_LENGTH ? parsed.fbclid : undefined,
      fbc: typeof parsed.fbc === "string" && parsed.fbc.length <= MAX_CLICK_ID_LENGTH && META_FBC_PATTERN.test(parsed.fbc) ? parsed.fbc : undefined,
      fbp: typeof parsed.fbp === "string" && parsed.fbp.length <= MAX_CLICK_ID_LENGTH && META_FBP_PATTERN.test(parsed.fbp) ? parsed.fbp : undefined,
      rdtCid: typeof parsed.rdtCid === "string" && parsed.rdtCid.length <= MAX_CLICK_ID_LENGTH ? parsed.rdtCid : undefined,
      twclid: typeof parsed.twclid === "string" && parsed.twclid.length <= MAX_CLICK_ID_LENGTH ? parsed.twclid : undefined,
    };
    return Object.values(clickIds).some(Boolean) ? clickIds : undefined;
  } catch {
    return undefined;
  }
}

export function clearMarketingClickIds(): void {
  try {
    sessionStorage.removeItem(MARKETING_CLICK_IDS_STORAGE_KEY);
  } catch {
    // Storage may be unavailable; fail closed without breaking navigation.
  }
}

/** Captures allowlisted click IDs only while explicit marketing consent remains active. */
export function captureCurrentMarketingClickIds(): MarketingClickIds | undefined {
  if (!isMarketingConsentAllowed()) {
    clearMarketingClickIds();
    return undefined;
  }
  const params = new URLSearchParams(window.location.search);
  const stored = storedClickIds();
  if (consentPreferences()?.analytics !== true && stored?.gaClientId) {
    delete stored.gaClientId;
    try {
      if (Object.values(stored).some(Boolean)) {
        sessionStorage.setItem(MARKETING_CLICK_IDS_STORAGE_KEY, JSON.stringify(stored));
      } else {
        sessionStorage.removeItem(MARKETING_CLICK_IDS_STORAGE_KEY);
      }
    } catch {
      // Storage may be unavailable; the in-memory value is still filtered below.
    }
  }
  const current: MarketingClickIds = {
    gaClientId: gaClientIdCookie(),
    fbclid: boundedParam(params, "fbclid"),
    fbc: metaFbcCookie(),
    fbp: metaFbpCookie(),
    rdtCid: boundedParam(params, "rdt_cid"),
    twclid: boundedParam(params, "twclid"),
  };
  if (!current.fbc && current.fbclid && (stored?.fbclid !== current.fbclid || !stored.fbc)) {
    current.fbc = fbcFromFbclid(current.fbclid);
  }
  const merged = { ...stored, ...Object.fromEntries(Object.entries(current).filter(([, value]) => value)) };
  if (Object.values(merged).some(Boolean)) {
    try {
      sessionStorage.setItem(MARKETING_CLICK_IDS_STORAGE_KEY, JSON.stringify(merged));
    } catch {
      // Continue with the current navigation's IDs when storage is unavailable.
    }
    return merged;
  }
  return undefined;
}

/** Returns only click IDs visible on the current consented deposit request. */
export function currentMarketingClickIds(): MarketingClickIds | undefined {
  return captureCurrentMarketingClickIds();
}
